import { useState, useCallback, useEffect } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, Badge } from '@devkit/ui';
import { KeyRound, RefreshCw, Copy, Download } from 'lucide-react';
import { generatePassword, estimateEntropy } from '@devkit/tools-security';
import { trackToolExecute, trackCopy, trackDownload } from '@devkit/analytics';

export default function PasswordGeneratorPage() {
  const [length, setLength] = useState(20);
  const [uppercase, setUppercase] = useState(true);
  const [lowercase, setLowercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [entropy, setEntropy] = useState(0);
  const [poolSize, setPoolSize] = useState(0);

  const generate = useCallback(() => {
    setError(null);
    const result = generatePassword({
      length,
      uppercase,
      lowercase,
      numbers,
      symbols,
      excludeAmbiguous,
    });
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to generate password.');
      setPassword('');
      setEntropy(0);
      setPoolSize(0);
      return;
    }
    setPassword(result.data);
    setPoolSize(result.poolSize ?? 0);
    setEntropy(estimateEntropy(length, result.poolSize ?? 0));
    trackToolExecute('password-generator', 'generate');
  }, [length, uppercase, lowercase, numbers, symbols, excludeAmbiguous]);

  // Generate initial password on mount
  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyPassword = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      trackCopy('password-generator');
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const downloadPassword = () => {
    const blob = new Blob([password], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `password-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackDownload('password-generator');
  };

  // Strength classification
  const strength =
    entropy >= 128
      ? { label: 'Very Strong', color: 'text-green-400 bg-green-500/10' }
      : entropy >= 90
      ? { label: 'Strong', color: 'text-blue-400 bg-blue-500/10' }
      : entropy >= 60
      ? { label: 'Moderate', color: 'text-amber-400 bg-amber-500/10' }
      : { label: 'Weak', color: 'text-red-400 bg-red-500/10' };

  return (
    <ToolLayout slug="password-generator">
      <Card>
        <CardContent className="p-6">
          {/* Generated Password */}
          <div className="mb-6 p-4 bg-surface/40 border border-slate-700/50 rounded-lg">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Generated Password
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-lg font-mono text-white bg-slate-900/60 px-4 py-3 rounded break-all">
                {password || 'Click "Generate" to create a password'}
              </code>
              <Button onClick={generate} className="gap-2 flex-shrink-0">
                <RefreshCw className="w-4 h-4" />
                Generate
              </Button>
            </div>
            {password && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className={`text-xs font-medium px-2 py-1 rounded ${strength.color}`}>
                  {strength.label}
                </span>
                <Badge variant="default">{Math.round(entropy)} bits entropy</Badge>
                <Badge variant="default">Pool: {poolSize}</Badge>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={copyPassword} className="gap-1">
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </Button>
                <Button variant="ghost" size="sm" onClick={downloadPassword} className="gap-1">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-300">
                  Length: <span className="text-primary font-bold">{length}</span>
                </label>
                <span className="text-xs text-slate-500">4 – 128 chars</span>
              </div>
              <input
                type="range"
                min="4"
                max="128"
                value={length}
                onChange={(e) => setLength(parseInt(e.target.value, 10))}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 p-3 bg-surface/40 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-surface/60">
                <input
                  type="checkbox"
                  checked={uppercase}
                  onChange={(e) => setUppercase(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                <div>
                  <div className="text-sm font-medium text-white">Uppercase (A-Z)</div>
                  <div className="text-xs text-slate-500">26 characters</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 bg-surface/40 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-surface/60">
                <input
                  type="checkbox"
                  checked={lowercase}
                  onChange={(e) => setLowercase(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                <div>
                  <div className="text-sm font-medium text-white">Lowercase (a-z)</div>
                  <div className="text-xs text-slate-500">26 characters</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 bg-surface/40 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-surface/60">
                <input
                  type="checkbox"
                  checked={numbers}
                  onChange={(e) => setNumbers(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                <div>
                  <div className="text-sm font-medium text-white">Numbers (0-9)</div>
                  <div className="text-xs text-slate-500">10 characters</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 bg-surface/40 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-surface/60">
                <input
                  type="checkbox"
                  checked={symbols}
                  onChange={(e) => setSymbols(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                <div>
                  <div className="text-sm font-medium text-white">Symbols (!@#$...)</div>
                  <div className="text-xs text-slate-500">28 characters</div>
                </div>
              </label>
            </div>

            <label className="flex items-center gap-3 p-3 bg-surface/40 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-surface/60">
              <input
                type="checkbox"
                checked={excludeAmbiguous}
                onChange={(e) => setExcludeAmbiguous(e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              <div>
                <div className="text-sm font-medium text-white">Exclude ambiguous characters</div>
                <div className="text-xs text-slate-500">Skip 0, O, o, 1, l, I, |</div>
              </div>
            </label>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-700">
            <Button onClick={generate} className="w-full gap-2" size="lg">
              <KeyRound className="w-5 h-5" />
              Generate New Password
            </Button>
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
